import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { KycListItem, fetchKycRecords } from 'src/api/adminKyc';

/** One entry per filter bar, keyed by the API query parameter it sends. */
export type KycFilters = Record<string, string>;

interface KycState {
  data: {
    kyc: KycListItem[];
    totalCount: number;
    totalPages: number;
    filters: KycFilters;
  };
  loading: boolean;
  error: string | null;
}

const initialState: KycState = {
  data: {
    kyc: [],
    totalCount: 0,
    totalPages: 0,
    filters: {},
  },
  loading: false,
  error: null,
};

export const fetchKyc = createAsyncThunk(
  'components/fetchKyc',
  async ({
    page,
    filters,
    status,
    provider,
    requiresManualReview,
  }: {
    page: number;
    /** Already debounced and trimmed; ANDed server-side. */
    filters?: KycFilters;
    status?: string;
    provider?: string;
    requiresManualReview?: boolean;
  }) => {
    const { name, email, country_code, id } = filters ?? {};

    const response = await fetchKycRecords({
      page,
      limit: 10,
      status: status || undefined,
      provider: provider || undefined,
      requires_manual_review: requiresManualReview ? true : undefined,
      name: name || undefined,
      email: email || undefined,
      country_code: country_code || undefined,
      id: id || undefined,
    });

    return {
      data: response.kyc,
      totalCount: response.meta.total_count,
      totalPages: response.meta.page_count,
      page,
    };
  },
);

const kycData = createSlice({
  name: 'kyc',
  initialState,
  reducers: {
    updateFilter: (state, action: { payload: { key: string; value: string } }) => {
      const { key, value } = action.payload;
      state.data.filters = { ...state.data.filters, [key]: value };
    },
    resetFilters: (state) => {
      state.data.filters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKyc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKyc.fulfilled, (state, action) => {
        // Filters belong to updateFilter/resetFilters. Echoing them back from
        // a resolved request would clobber whatever the user has typed since
        // it was fired.
        state.data.kyc = action.payload.data;
        state.data.totalCount = action.payload.totalCount;
        state.data.totalPages = action.payload.totalPages;
        state.loading = false;
      })
      .addCase(fetchKyc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch kyc';
      });
  },
});

export const { updateFilter, resetFilters } = kycData.actions;

export default kycData.reducer;
